/**
 * useBreakpoint — Shared responsive breakpoint hook.
 *
 * Extracted from AdaptiveLayout (Phase 15.7.10d) for reuse across components
 * that need responsive behavior (e.g., Sheet side switching, conditional
 * rendering, layout adjustments).
 *
 * Breakpoints match the Tailwind defaults plus AI Farm desktop convention:
 *   - Mobile:  < 768px
 *   - Tablet:  768-1279px
 *   - Desktop: >= 1280px
 *
 * Returns { bp, isMobile, isTablet, isDesktop, width }.
 *   bp — string: 'mobile' | 'tablet' | 'desktop'
 *   isMobile/isTablet/isDesktop — boolean convenience flags
 *   width — current window.innerWidth
 *
 * Server-side safe: returns sensible defaults when window is undefined.
 */

import { useState, useEffect } from 'react';

const BP_TABLET = 768;
const BP_DESKTOP = 1280;

function getBreakpointState() {
  const w = typeof window !== 'undefined' ? window.innerWidth : BP_DESKTOP;
  return {
    bp: w >= BP_DESKTOP ? 'desktop' : w >= BP_TABLET ? 'tablet' : 'mobile',
    isMobile: w < BP_TABLET,
    isTablet: w >= BP_TABLET && w < BP_DESKTOP,
    isDesktop: w >= BP_DESKTOP,
    width: w,
  };
}

export function useBreakpoint() {
  const [state, setState] = useState(() => getBreakpointState());

  useEffect(() => {
    const handleResize = () => setState(getBreakpointState());
    window.addEventListener('resize', handleResize, { passive: true });
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return state;
}
